(() => {
  const closeMorphDuration = 240;
  const closeFadeDuration = 80;
  const pageScrollSettleDelay = 180;
  const desktopBreakpoint = '(min-width: 990px)';
  const finePointerQuery = '(hover: hover) and (pointer: fine)';

  const createMenuCoordinator = () => {
    let activeController = null;
    let pageIsScrolling = false;
    let hoverResumePending = false;
    let pageScrollTimer = 0;

    window.addEventListener('scroll', () => {
      if (!window.matchMedia(desktopBreakpoint).matches) return;

      pageIsScrolling = true;
      hoverResumePending = true;
      window.clearTimeout(pageScrollTimer);
      pageScrollTimer = window.setTimeout(() => {
        pageIsScrolling = false;
        pageScrollTimer = 0;
      }, pageScrollSettleDelay);

      activeController?.closeMenu();
    }, { passive: true });

    return {
      activate(controller) {
        if (activeController === controller) return;

        const previousController = activeController;
        activeController = controller;
        previousController?.closeMenu({ immediate: true });
      },
      release(controller) {
        if (activeController === controller) activeController = null;
      },
      isPageScrolling() {
        return pageIsScrolling;
      },
      consumeHoverResume() {
        if (pageIsScrolling || !hoverResumePending) return false;

        hoverResumePending = false;
        return true;
      },
    };
  };

  const initialiseMenuMorph = (details, coordinator) => {
    if (!details || details.dataset.noirMenuMorphInitialised === 'true') return;

    const summary = details.querySelector(':scope > summary');
    const panel = details.querySelector(':scope > .noir-products-menu__content, :scope > .header__submenu');

    if (!summary || !panel) return;

    let closeTimer = 0;
    let closeFadeTimer = 0;
    let openFrame = 0;
    let lastPointerDownAt = 0;
    let focusOrigin = 'none';
    let clickLocked = false;

    const isDesktop = () => window.matchMedia(desktopBreakpoint).matches;
    const isFinePointer = () => window.matchMedia(finePointerQuery).matches;
    const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const getState = () => details.dataset.noirMenuState || 'closed';
    const setState = (state) => {
      details.dataset.noirMenuState = state;
    };
    const isWithinRect = (x, y, rect) => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    const handleLockedPointerMove = (event) => {
      if (!clickLocked || !isDesktop() || event.pointerType === 'touch' || hasKeyboardFocus()) return;

      const triggerRect = summary.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const inTrigger = isWithinRect(event.clientX, event.clientY, triggerRect);
      const inPanel = isWithinRect(event.clientX, event.clientY, panelRect);
      const bridgeTop = Math.min(triggerRect.bottom, panelRect.top);
      const bridgeBottom = Math.max(triggerRect.bottom, panelRect.top);
      const bridgeLeft = Math.min(triggerRect.left, panelRect.left);
      const bridgeRight = Math.max(triggerRect.right, panelRect.right);
      const inSupportBridge = details.classList.contains('noir-support-menu')
        && event.clientX >= bridgeLeft
        && event.clientX <= bridgeRight
        && event.clientY >= bridgeTop
        && event.clientY <= bridgeBottom;

      if (!inTrigger && !inPanel && !inSupportBridge) closeMenu();
    };
    const setClickLocked = (locked) => {
      if (clickLocked === locked) return;
      clickLocked = locked;
      if (locked) {
        details.dataset.noirMenuClickLocked = 'true';
        document.addEventListener('pointermove', handleLockedPointerMove, true);
      } else {
        delete details.dataset.noirMenuClickLocked;
        document.removeEventListener('pointermove', handleLockedPointerMove, true);
      }
    };
    const clearPendingMotion = () => {
      window.clearTimeout(closeTimer);
      window.clearTimeout(closeFadeTimer);
      window.cancelAnimationFrame(openFrame);
      closeTimer = 0;
      closeFadeTimer = 0;
      openFrame = 0;
    };

    const openMenu = ({ instant = false } = {}) => {
      coordinator.activate(controller);
      clearPendingMotion();
      details.open = true;
      const immediate = instant || prefersReducedMotion();
      setState(immediate ? 'open' : 'opening');

      if (immediate) return;

      openFrame = window.requestAnimationFrame(() => {
        openFrame = 0;
        if (details.open && getState() !== 'closing') setState('open');
      });
    };

    const closeMenu = ({ immediate = false } = {}) => {
      setClickLocked(false);
      if ((getState() === 'closing' || getState() === 'closing-final') && !immediate) return;
      clearPendingMotion();
      if (!details.open && getState() === 'closed') return;

      if (immediate || prefersReducedMotion()) {
        details.open = false;
        setState('closed');
        coordinator.release(controller);
        return;
      }

      details.open = true;
      setState('closing');
      const morphDuration = closeMorphDuration;
      const fadeDuration = closeFadeDuration;
      const fadeStart = Math.max(0, morphDuration - fadeDuration);

      closeTimer = window.setTimeout(() => {
        if (!details.open || getState() !== 'closing') return;

        setState('closing-final');
        closeTimer = 0;
        closeFadeTimer = window.setTimeout(() => {
          if (!details.open || getState() !== 'closing-final') return;

          details.open = false;
          setState('closed');
          closeFadeTimer = 0;
          coordinator.release(controller);
        }, fadeDuration);
      }, fadeStart);
    };

    const controller = { closeMenu };

    const wasPointerActivated = () => performance.now() - lastPointerDownAt < 500;
    const hasKeyboardFocus = () => details.contains(document.activeElement) && focusOrigin === 'keyboard';

    details.dataset.noirMenuState = 'closed';

    details.addEventListener('pointerdown', () => {
      lastPointerDownAt = performance.now();
      focusOrigin = 'pointer';
    });

    details.addEventListener('pointerenter', (event) => {
      if (!isDesktop() || !isFinePointer() || event.pointerType === 'touch' || coordinator.isPageScrolling()) return;
      coordinator.consumeHoverResume();
      openMenu();
    });

    details.addEventListener('pointermove', (event) => {
      if (!isDesktop() || !isFinePointer() || event.pointerType === 'touch' || coordinator.isPageScrolling()) return;
      if (!coordinator.consumeHoverResume() || !details.matches(':hover')) return;
      if (getState() === 'closed' || getState() === 'closing' || getState() === 'closing-final') openMenu();
    });

    details.addEventListener('pointerleave', () => {
      if (!isDesktop() || !isFinePointer() || hasKeyboardFocus()) return;
      if (clickLocked) return;
      closeMenu();
    });

    details.addEventListener('focusin', (event) => {
      if (!isDesktop()) return;
      focusOrigin = wasPointerActivated() ? 'pointer' : 'keyboard';
      if (event.target !== summary && !summary.contains(event.target)) {
        if (getState() === 'closing' || getState() === 'closing-final') openMenu({ instant: true });
        return;
      }

      const keyboardFocus = summary.matches(':focus-visible') && !wasPointerActivated();
      openMenu({ instant: keyboardFocus });
    });

    details.addEventListener('focusout', (event) => {
      if (!isDesktop() || (event.relatedTarget && details.contains(event.relatedTarget))) return;
      focusOrigin = 'none';
      if (!details.matches(':hover')) closeMenu();
    });

    summary.addEventListener('click', (event) => {
      if (!isDesktop()) return;

      event.preventDefault();
      const isOpen = details.open && getState() !== 'closing' && getState() !== 'closing-final';
      if (clickLocked && isOpen) {
        closeMenu();
        return;
      }

      setClickLocked(true);
      const keyboardToggle = summary.matches(':focus-visible') && !wasPointerActivated();
      openMenu({ instant: keyboardToggle });
    });

    details.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !isDesktop()) return;

      event.preventDefault();
      closeMenu();
      summary.focus();
    });

    details.addEventListener('toggle', () => {
      if (!isDesktop()) return;
      if (!details.open && (getState() === 'closing' || getState() === 'closing-final')) {
        details.open = true;
        return;
      }
      if (details.open && getState() === 'closed') {
        openMenu({ instant: summary.matches(':focus-visible') });
      } else if (!details.open && getState() !== 'closed' && getState() !== 'closing') {
        closeMenu();
      }
    });

    details.dataset.noirMenuMorphInitialised = 'true';

    if (details.open) openMenu({ instant: true });
  };

  const initialiseProductsMenus = () => {
    const coordinator = createMenuCoordinator();

    document.querySelectorAll('.noir-products-menu').forEach((menu) => {
      const recommendations = menu.querySelector('[data-noir-recommendations]');
      const productGrid = recommendations?.querySelector('[data-noir-product-grid]');
      const recommendationTitle = recommendations?.querySelector('[data-noir-recommendation-title]');
      const details = menu.matches('details') ? menu : menu.querySelector('details');

      if (!details) return;

      initialiseMenuMorph(details, coordinator);

      if (menu.dataset.noirProductsInitialised === 'true') return;
      if (!recommendations || !productGrid || !recommendationTitle) {
        menu.dataset.noirProductsInitialised = 'true';
        return;
      }

      const defaultMarkup = productGrid.innerHTML;
      const featuredLabel = recommendations.dataset.noirFeaturedLabel || '';

      const resetRecommendations = () => {
        productGrid.innerHTML = defaultMarkup;
        recommendationTitle.textContent = featuredLabel;
        recommendations.removeAttribute('data-noir-active-collection');
      };

      const activateCollection = (trigger) => {
        const key = trigger.dataset.noirCollectionKey;
        const template = key ? document.getElementById(`NoirProductsMenu-Collection-${key}`) : null;

        if (!template || recommendations.dataset.noirActiveCollection === key) return;

        productGrid.replaceChildren(template.content.cloneNode(true));
        recommendationTitle.textContent = trigger.dataset.noirCollectionTitle || featuredLabel;
        recommendations.dataset.noirActiveCollection = key;
      };

      menu.querySelectorAll('[data-noir-collection-key]').forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => activateCollection(trigger));
        trigger.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') return;
          activateCollection(trigger);
        });
        trigger.addEventListener('focus', () => activateCollection(trigger));
      });

      menu.addEventListener('mouseleave', () => {
        if (!menu.contains(document.activeElement)) resetRecommendations();
      });
      menu.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !menu.contains(event.relatedTarget)) resetRecommendations();
      });
      details.addEventListener('toggle', () => {
        if (!details.open) resetRecommendations();
      });
      menu.dataset.noirProductsInitialised = 'true';
    });

    document.querySelectorAll('.noir-support-menu').forEach((details) => {
      initialiseMenuMorph(details, coordinator);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseProductsMenus, { once: true });
  } else {
    initialiseProductsMenus();
  }
})();
